import { blueDarkest, inkLighter, primaryDarkest, redDarkest, yellowDarkest } from '../../../../styles/design-tokens';
import type { GoogleDriveFile } from '../../../../api/google-integrations-client/google-integrations-client.type';

/** Presentation metadata derived from a file's MIME type. */
interface GoogleFilePreview {
  /** Human-readable file-type label for the card/modal header. */
  label: string;
  /** `material-symbols` icon name for the file type. */
  icon: string;
  /** The read-only, iframe-embeddable Google preview URL. */
  previewUrl: string;
  /** Brand-aligned accent color for the icon and label, matching `DOC_TYPE_CONFIG`'s file-type colors. */
  color: string;
  /** Stable file-type key used by the Documents file-type filter, e.g. `gdoc`. */
  typeKey: string;
}

/**
 * Single source of truth per MIME type: label, icon, accent color, filter
 * `typeKey`, and — for Google-native types that have a first-party
 * `docs.google.com/.../preview` viewer — the URL path segment (`document`,
 * `spreadsheets`, `presentation`). Types without a `nativePath` fall back to
 * the generic Drive file viewer.
 */
const MIME_TYPE_META: Record<
  string,
  { label: string; icon: string; color: string; typeKey: string; nativePath?: string }
> = {
  'application/vnd.google-apps.document': {
    label: 'Google Doc',
    icon: 'material-symbols:description',
    color: blueDarkest,
    typeKey: 'gdoc',
    nativePath: 'document',
  },
  'application/vnd.google-apps.spreadsheet': {
    label: 'Google Sheet',
    icon: 'material-symbols:table',
    color: primaryDarkest,
    typeKey: 'gsheet',
    nativePath: 'spreadsheets',
  },
  'application/vnd.google-apps.presentation': {
    label: 'Google Slides',
    icon: 'material-symbols:slideshow',
    color: yellowDarkest,
    typeKey: 'gslides',
    nativePath: 'presentation',
  },
  'application/vnd.google-apps.folder': {
    label: 'Folder',
    icon: 'material-symbols:folder',
    color: inkLighter,
    typeKey: 'gfolder',
  },
  // Shares the `pdf` typeKey with Flora-generated PDFs so the file-type filter
  // treats all PDFs as one type regardless of source.
  'application/pdf': { label: 'PDF', icon: 'material-symbols:picture-as-pdf', color: redDarkest, typeKey: 'pdf' },
};

/**
 * Whether a Drive file can be shown in an inline preview iframe. Only Drive
 * folders are excluded — every other type resolves to either a Google-native
 * `/preview` or the generic Drive file viewer.
 *
 * @param {GoogleDriveFile} file - The Drive file to test.
 * @returns {boolean} True when {@link getGoogleFilePreview} yields a viewer URL.
 */
function isPreviewable(file: GoogleDriveFile): boolean {
  return file.mimeType !== 'application/vnd.google-apps.folder';
}

/**
 * Resolve the label, icon, and read-only preview URL for a Drive file.
 *
 * Google-native types (Docs/Sheets/Slides) use their first-party
 * `docs.google.com/.../preview` viewer; everything else falls back to the
 * generic `drive.google.com/file/d/<id>/preview`, which renders PDFs, images,
 * and uploads (and shows Drive's own no-preview state for the rare unsupported
 * file). Both hosts are already allow-listed in the app's CSP `frame-src`.
 *
 * @param {GoogleDriveFile} file - The Drive file to present.
 * @returns {GoogleFilePreview} The label, icon, color, and preview URL.
 */
function getGoogleFilePreview(file: GoogleDriveFile): GoogleFilePreview {
  const meta = MIME_TYPE_META[file.mimeType] ?? {
    label: 'File',
    icon: 'material-symbols:draft',
    color: inkLighter,
    typeKey: 'gfile',
  };
  const previewUrl = meta.nativePath
    ? `https://docs.google.com/${meta.nativePath}/d/${file.id}/preview`
    : `https://drive.google.com/file/d/${file.id}/preview`;

  return { label: meta.label, icon: meta.icon, color: meta.color, typeKey: meta.typeKey, previewUrl };
}

export { getGoogleFilePreview, isPreviewable };
